/**
 * Краски и размер букв одного Блока (правка 2026-08-30).
 *
 * Панель открывается из меню Блока и живёт прямо в карточке, а не в отдельном окне: человек
 * выбирает цвет и тут же видит, что получилось, — на самом Блоке, а не на квадратике
 * образца. Настройка личная: сосед по Каналу видит Блок в общих красках.
 *
 * Про доступность. Цвет здесь не украшение, а способ различать Блоки глазами, и потому
 * пара «буквы на фоне» проверяется на различимость (WCAG 1.4.3) и о слабой паре говорится
 * словами. Не запрещаем — запрет отобрал бы у человека его же выбор, — но и молчать нельзя:
 * тому, кто плохо видит, серые буквы на белом однажды обойдутся потерянным Сообщением.
 * Сами цвета выбираются из готового набора, а не пипеткой: набор проверен на пригодность,
 * и в нём нельзя ошибиться на один знак.
 */
import type { ReactNode } from 'react';
import type { BlockTextScale, BlockView } from '@core/layout/editor.js';
import { contrastRatio, ENOUGH_CONTRAST } from './contrast.js';
import { Button } from '../ui/controls.js';
import { useT } from '../state/settings.js';
import type { PhraseKey } from '../i18n/dictionary.js';

/** Готовый набор красок: светлые для фона, тёмные для букв, но выбор за человеком. */
export const BLOCK_COLOURS: ReadonlyArray<{ readonly value: string; readonly key: PhraseKey }> = [
  { value: '#ffffff', key: 'colour.white' },
  { value: '#f3f4f6', key: 'colour.lightGrey' },
  { value: '#fde68a', key: 'colour.sand' },
  { value: '#bbf7d0', key: 'colour.mint' },
  { value: '#bfdbfe', key: 'colour.sky' },
  { value: '#e9d5ff', key: 'colour.lilac' },
  { value: '#fecaca', key: 'colour.rose' },
  { value: '#111827', key: 'colour.almostBlack' },
  { value: '#374151', key: 'colour.darkGrey' },
  { value: '#1e3a8a', key: 'colour.darkBlue' },
  { value: '#14532d', key: 'colour.darkGreen' },
  { value: '#7f1d1d', key: 'colour.darkRed' },
];

const SCALES: readonly BlockTextScale[] = ['small', 'normal', 'large', 'huge'];

/**
 * Ряд красок. Это выбор одного из многих, поэтому и объявляется он выбором: диктор
 * скажет «сиреневый, выбран, шестой из тринадцати», а не «кнопка».
 */
function Palette({
  label,
  chosen,
  onPick,
}: {
  readonly label: string;
  readonly chosen: string | null;
  readonly onPick: (colour: string | null) => void;
}): ReactNode {
  const t = useT();
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-ink-soft">{label}</span>
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1">
        <button
          type="button"
          role="radio"
          aria-checked={chosen === null}
          aria-label={`${label}: ${t('block.colourShared')}`}
          onClick={() => onPick(null)}
          className={`rounded border px-2 py-1 text-xs ${
            chosen === null ? 'border-accent ring-2 ring-[var(--ktm-accent)]' : 'border-line'
          }`}
        >
          {t('block.colourShared')}
        </button>
        {BLOCK_COLOURS.map((colour) => (
          <button
            key={colour.value}
            type="button"
            role="radio"
            aria-checked={chosen === colour.value}
            aria-label={t(colour.key)}
            title={t(colour.key)}
            onClick={() => onPick(colour.value)}
            style={{ backgroundColor: colour.value }}
            // Размер квадратика — от размера текста: при крупном шрифте из Настроек в него
            // должно быть так же легко попасть, как и в остальные кнопки.
            className={`size-[1.75em] rounded border ${
              chosen === colour.value ? 'border-accent ring-2 ring-[var(--ktm-accent)]' : 'border-line'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export function BlockSettings({
  view,
  onChange,
  onClose,
  busy = false,
}: {
  readonly view: BlockView;
  readonly onChange: (patch: {
    background?: string | null;
    foreground?: string | null;
    textScale?: BlockTextScale | null;
  }) => void;
  readonly onClose: () => void;
  readonly busy?: boolean;
}): ReactNode {
  const t = useT();
  // Считаем по тому, что человек выбрал сам. Общие цвета берутся из темы и меняются вместе
  // с ней — их различимость уже проверена там, и подставлять сюда сегодняшний оттенок темы
  // значило бы пугать человека тем, чего он не выбирал.
  const ratio =
    view.background && view.foreground ? contrastRatio(view.foreground, view.background) : null;
  const weak = ratio !== null && ratio < ENOUGH_CONTRAST;

  return (
    <div className="flex flex-col gap-2 border-b border-line bg-surface-2 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-medium text-ink">{t('block.settings')}</h4>
        <Button look="quiet" onClick={onClose} aria-label={t('common.close')} className="px-2 py-0.5">
          ✕
        </Button>
      </div>

      <Palette
        label={t('block.background')}
        chosen={view.background}
        onPick={(colour) => onChange({ background: colour })}
      />
      <Palette
        label={t('block.foreground')}
        chosen={view.foreground}
        onPick={(colour) => onChange({ foreground: colour })}
      />

      <div className="flex flex-col gap-1">
        <span className="text-xs text-ink-soft">{t('block.textScale')}</span>
        <div role="radiogroup" aria-label={t('block.textScale')} className="flex flex-wrap gap-1">
          <button
            type="button"
            role="radio"
            aria-checked={view.textScale === null}
            // Подпись отдельная от красок: «как у всех» звучит одинаково, но человек должен
            // слышать, о чём именно речь, — иначе, дойдя стрелками до второго такого пункта,
            // он не поймёт, вернёт он цвет или размер.
            aria-label={`${t('block.textScale')}: ${t('block.colourShared')}`}
            onClick={() => onChange({ textScale: null })}
            className={`rounded border px-2 py-1 text-xs ${
              view.textScale === null ? 'border-accent ring-2 ring-[var(--ktm-accent)]' : 'border-line'
            }`}
          >
            {t('block.colourShared')}
          </button>
          {SCALES.map((scale) => (
            <button
              key={scale}
              type="button"
              role="radio"
              aria-checked={view.textScale === scale}
              aria-label={`${t('block.textScale')}: ${t(`block.scale.${scale}`)}`}
              onClick={() => onChange({ textScale: scale })}
              className={`rounded border px-2 py-1 text-xs ${
                view.textScale === scale ? 'border-accent ring-2 ring-[var(--ktm-accent)]' : 'border-line'
              }`}
            >
              {t(`block.scale.${scale}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Предупреждение объявляется голосом само: человек, выбирающий цвет вслепую, иначе
          узнал бы о слабой паре только от зрячего соседа. */}
      {weak ? (
        <p role="status" aria-live="polite" className="text-xs text-danger">
          {t('block.weakContrast')}
        </p>
      ) : null}

      <div>
        <Button
          look="plain"
          disabled={busy}
          onClick={() => onChange({ background: null, foreground: null, textScale: null })}
        >
          {t('block.resetColours')}
        </Button>
      </div>
    </div>
  );
}
